import React from 'react';
import { describeError, reportClientError } from './reportClientError';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Changing this clears a caught error, so a route change recovers without a reload. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
  resetKey?: string;
}

/**
 * Catches render failures so one throw cannot leave a blank document.
 *
 * The fallback is the whole page rather than the `DashboardFailureModal`
 * treatment: once a subtree has thrown, there is no working app left behind a
 * scrim to dismiss back to.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return { error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportClientError('boundary', error, info.componentStack ?? undefined);
  }

  private reload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { message } = describeError(error);

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-paper p-4">
        <div
          role="alert"
          aria-label="The app stopped"
          className="flex w-full max-w-xl flex-col items-center text-center"
        >
          <p className="text-base text-ink">The app stopped.</p>
          <p className="mt-3 max-w-lg text-sm leading-6 text-muted">{message}</p>
          <button
            type="button"
            onClick={this.reload}
            className="mt-10 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
